import { useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import useGsapSection from '../../hooks/useGsapSection';
import useAvailabilityLabel from '../../hooks/useAvailabilityLabel';
import { ScrambleReveal, ClipRevealText } from '../TextEffects';
import ContactTrigger from '../ContactTrigger';
import { SOCIAL_LINKS } from '../../data/social';
import './ContactSection.css';

/* ───────────────────────────────────────────────────────────────
   MagneticTitle — soft per-letter magnet + staggered fade-up entry
   • Entrance: each character fades up sequentially when the title
     enters the viewport (own useInView).
   • Hover: each character reacts to cursor proximity with a gentle
     translate + subtle scale + micro-rotation (quadratic falloff).
   • Entrance and hover transforms are on different DOM layers so
     they compose without fighting.
   ─────────────────────────────────────────────────────────────── */
const titleParts = [
  { text: "Let's build what's next.", italic: false },
];

const MagneticTitle = ({ className, externalInView }) => {
  const ref = useRef(null);
  const selfInView = useInView(ref, {
    once: true,
    margin: '0px 0px 30% 0px',
    amount: 0,
  });
  const inView = externalInView ?? selfInView;
  const cache = useRef([]);

  const buildCache = useCallback(() => {
    if (!ref.current) return;
    const nodes = ref.current.querySelectorAll('.cta-title-char');
    cache.current = Array.from(nodes).map((node) => {
      const r = node.getBoundingClientRect();
      return {
        node,
        cx: r.left + r.width / 2,
        cy: r.top + r.height / 2,
      };
    });
  }, []);

  const handleMove = useCallback((e) => {
    if (cache.current.length === 0) buildCache();
    const RADIUS = 150;
    const cx = e.clientX;
    const cy = e.clientY;
    cache.current.forEach(({ node, cx: nx, cy: ny }) => {
      const dx = cx - nx;
      const dy = cy - ny;
      const dist = Math.hypot(dx, dy);
      if (dist < RADIUS) {
        const f = Math.pow(1 - dist / RADIUS, 2);
        // soft repel on X, gentle lift on Y, mild scale, micro rotation
        const tx = -dx * 0.10 * f;
        const ty = -dy * 0.28 * f;
        const sc = 1 + 0.22 * f;
        const rot = dx * 0.022 * f;
        node.style.transform =
          `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px) scale(${sc.toFixed(3)}) rotate(${rot.toFixed(2)}deg)`;
      } else if (node.style.transform) {
        node.style.transform = '';
      }
    });
  }, [buildCache]);

  const handleEnter = useCallback(() => {
    buildCache();
  }, [buildCache]);

  const handleLeave = useCallback(() => {
    cache.current.forEach(({ node }) => {
      node.style.transform = '';
    });
  }, []);

  // Split & render — each char gets a 2-layer wrapper:
  // outer  → motion.span : staggered fade-up entrance
  // inner  → span        : magnetic transform target (ref via querySelectorAll)
  let globalIdx = 0;
  const content = titleParts.map((part, pIdx) => {
    const words = part.text.split(' ');
    return words.map((word, wIdx) => {
      const wordKey = `p${pIdx}-w${wIdx}`;
      if (word.length === 0) {
        return (
          <span key={`sp-${wordKey}`} className="cta-title-space" aria-hidden="true">
            {'\u00A0'}
          </span>
        );
      }
      const chars = Array.from(word).map((ch, cIdx) => {
        const i = globalIdx++;
        return (
          <motion.span
            key={`${wordKey}-c${cIdx}`}
            className="cta-title-char-reveal"
            initial={{ y: 36, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : { y: 36, opacity: 0 }}
            transition={{
              duration: 0.95,
              delay: 0.12 + i * 0.022,
              ease: [0.16, 1, 0.3, 1],
            }}
            aria-hidden="true"
          >
            <span className={`cta-title-char${part.italic ? ' is-em' : ''}`}>
              {ch}
            </span>
          </motion.span>
        );
      });
      const showTrailingSpace =
        !(pIdx === titleParts.length - 1 && wIdx === words.length - 1);
      return (
        <span key={wordKey} className="cta-title-word">
          {chars}
          {showTrailingSpace && (
            <span className="cta-title-space" aria-hidden="true">
              {'\u00A0'}
            </span>
          )}
        </span>
      );
    });
  });

  const a11yText = titleParts.map((p) => p.text).join('');

  return (
    <h2
      ref={ref}
      className={className}
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      data-testid="cta-title"
    >
      <span className="sr-only">{a11yText}</span>
      {content}
    </h2>
  );
};

const contactGsapSetup = (gsap, _scope, scrollOpts) => {
  gsap.from('.cta-footer-line', {
    scaleX: 0,
    duration: 1.4,
    delay: 0.1,
    ease: 'power3.out',
    transformOrigin: 'left center',
    scrollTrigger: scrollOpts,
  });
  gsap.from('.cta-signoff', {
    opacity: 0,
    y: 60,
    duration: 1.1,
    delay: 0.4,
    ease: 'power3.out',
    scrollTrigger: scrollOpts,
  });
};

const ContactSection = () => {
  const ref = useRef(null);
  const inView = useGsapSection(ref, { setup: contactGsapSetup });

  const { label: availabilityLabel, year } = useAvailabilityLabel();

  return (
    <footer
      id="contact"
      ref={ref}
      className="cta-footer"
      data-testid="contact-section"
    >
      <div className="cta-footer-line" aria-hidden="true" />

      <div className="cta-strip">
        <span className="cta-strip-left gsap-reveal">
          <span className="cta-strip-num">(007)</span>
          <span className="cta-strip-divider" />
          <ClipRevealText text="Contact" delay={0.05} externalInView={inView} />
        </span>
        <span className="cta-strip-right gsap-reveal">
          <span className="cta-dot" aria-hidden="true" />
          <ScrambleReveal text={availabilityLabel} delay={0.15} externalInView={inView} />
        </span>
      </div>

      <div className="cta-title-wrap">
        <MagneticTitle className="cta-title" externalInView={inView} />
      </div>

      <ContactTrigger className="cta-email gsap-reveal" data-testid="contact-open">
        <span className="cta-email-text">Contact</span>
        <span className="cta-email-arrow" aria-hidden="true">↗</span>
      </ContactTrigger>

      {/* Meta grid */}
      <div className="cta-meta">
        <div className="cta-col">
          <ScrambleReveal text="— Status" className="cta-col-label" delay={0.06} externalInView={inView} />
          <span className="cta-col-value cta-col-value-status">
            <span className="cta-dot" aria-hidden="true" />
            Open for new projects
          </span>
        </div>

        <div className="cta-col">
          <ScrambleReveal text="— Follow" className="cta-col-label" delay={0.1} externalInView={inView} />
          <ul className="cta-socials">
            {SOCIAL_LINKS.map((s) => (
              <li key={s.id}>
                <a
                  className="cta-social"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{s.label}</span>
                  <span aria-hidden="true">↗</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="cta-col cta-col-right">
          <ScrambleReveal text="— Colophon" className="cta-col-label" delay={0.14} externalInView={inView} />
          <span className="cta-col-value">Set in Inter & IBM Plex Mono.</span>
          <span className="cta-col-meta">Hand-coded.</span>
        </div>
      </div>

      <div className="cta-signoff" aria-hidden="true">
        Together.
      </div>

      {/* Bottom rail — copyright (left) · domain (center) · back-to-top (right) */}
      <div className="cta-bottom">
        <span className="cta-bottom-item cta-bottom-left">
          © {year} Andrea Fagioli — All rights reserved.{' '}
          <Link to="/privacy" className="cta-bottom-privacy">
            Privacy
          </Link>
        </span>
        <a
          href="/"
          className="cta-bottom-domain"
          data-testid="cta-domain"
        >
          andreafagioli.com
        </a>
        <a
          href="#top"
          className="cta-bottom-back"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          Back to top <span aria-hidden="true">↑</span>
        </a>
      </div>
    </footer>
  );
};

export default ContactSection;
